/**
 * The workspace shell: sidebar, site switcher, live-connection indicator, entitlement banner. The shell is
 * deliberately dull (L1, DESIGN.md §7) — everything interesting should be in the page body.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api, type Me } from '../lib/api.js';
import { cx } from './ui.js';
import { useLiveStream, type StreamState } from '../lib/use-stream.js';
import { ERRORS_BEFORE_POLL, POLL_INTERVAL_MS } from '../lib/stream-resilience.js';
import { bandStyle, money } from '../lib/format.js';
import type { EntitlementState } from '../lib/types.js';

interface NavEntry {
  to: string;
  label: string;
  end?: boolean;
  roles?: string[];
  group: 'watch' | 'act' | 'configure' | 'account';
}

const NAV: NavEntry[] = [
  { to: '/app', label: 'Fleet wall', end: true, group: 'watch' },
  { to: '/app/anomalies', label: 'Anomalies', group: 'watch' },
  { to: '/app/drafts', label: 'Work-order drafts', group: 'act' },
  { to: '/app/pilot', label: 'Pilot scoreboard', group: 'act' },
  { to: '/app/knowledge', label: 'Knowledge graph', roles: ['admin', 'reliability_engineer'], group: 'configure' },
  { to: '/app/ingest', label: 'Data connections', roles: ['admin', 'reliability_engineer', 'operations'], group: 'configure' },
  { to: '/app/system', label: 'System health', roles: ['admin', 'reliability_engineer'], group: 'configure' },
  { to: '/app/admin', label: 'Users & plans', roles: ['admin'], group: 'configure' },
  { to: '/app/account', label: 'Plan & usage', group: 'account' },
];

const GROUP_LABEL: Record<NavEntry['group'], string> = { watch: 'Watch', act: 'Act', configure: 'Configure', account: 'Account' };

export function AppShell({ me, children }: { me: Me; children?: ReactNode }): ReactNode {
  useNavScrollState();
  const navigate = useNavigate();
  const [entitlement, setEntitlement] = useState<EntitlementState | null>(null);
  const live = useLiveStream();

  useEffect(() => {
    let cancelled = false;
    void api
      .get<{ entitlement: EntitlementState | null }>('/account/entitlement')
      .then((result) => {
        if (!cancelled) setEntitlement(result.entitlement);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [me.activeSiteId]);

  const signOut = async (): Promise<void> => {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-By': 'turbinepulse-app' } }).catch(() => undefined);
    navigate('/login?signed_out=1', { replace: true });
  };

  const role = me.roleOnActiveSite ?? me.role;
  const entries = NAV.filter((entry) => !entry.roles || entry.roles.includes(role));
  const groups: NavEntry['group'][] = ['watch', 'act', 'configure', 'account'];

  return (
    <div className="min-h-screen bg-bg">
      <header className="nav" data-scrolled="false">
        <div className="mx-auto flex max-w-[1440px] items-center gap-4 px-3 py-3 md:px-6">
          <a href="/app" className="flex items-center gap-2 no-underline">
            <img src="/logo.png" alt="" width={24} height={24} className="brand-mark" />
            <span className="font-display text-[15px] font-semibold tracking-tight text-text">TurbinePulse</span>
          </a>
          <SiteSwitcher me={me} />
          <div className="ml-auto flex items-center gap-3">
            <LiveIndicator state={live.state} events={live.count} />
            {me.sites.length > 1 ? null : <span className="hidden md:inline text-cell text-text-tertiary">{me.sites[0]?.name}</span>}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
              Sign out
            </button>
            <span className="hidden sm:grid h-8 w-8 place-items-center rounded-full bg-accent-subtle text-[12px] font-semibold text-accent-active" title={`${me.displayName ?? me.email ?? ''} · ${role}`}>
              {(me.displayName ?? me.email ?? '?').slice(0, 1).toUpperCase()}
            </span>
          </div>
        </div>
        <EntitlementBanner entitlement={entitlement} />
      </header>

      <div className="mx-auto flex max-w-[1440px] gap-4 px-3 py-4 md:px-6">
        <nav aria-label="Workspace" className="hidden w-[212px] shrink-0 flex-col gap-4 lg:flex">
          {groups.map((group) => {
            const items = entries.filter((entry) => entry.group === group);
            if (items.length === 0) return null;
            return (
              <div key={group}>
                <p className="micro-label mb-1 px-2">{GROUP_LABEL[group]}</p>
                <div className="grid gap-[2px]">
                  {items.map((entry) => (
                    <NavLink key={entry.to} to={entry.to} end={entry.end} className="side-item">
                      {entry.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="mt-2 border-t border-border pt-3">
            <a className="side-item" href="/how-it-works">
              How it works
            </a>
            <p className="mt-2 px-2 text-[11px] leading-relaxed text-text-tertiary">
              Every number in this workspace is computed from your stored signals. Nothing here is a demo figure.
            </p>
          </div>
        </nav>

        {/* Mobile / tablet: horizontal scroll-snap strip rather than a hidden menu (DESIGN.md §9). */}
        <nav aria-label="Workspace sections" className="scroll-strip w-full lg:hidden" style={{ marginBottom: -4 }}>
          {items0(entries).map((entry) => (
            <NavLink key={entry.to} to={entry.to} end={entry.end} className={({ isActive }) => cx('tag shrink-0 no-underline', isActive && 'tag--ok')}>
              {entry.label}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1 fade-in">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}

const items0 = (entries: NavEntry[]): NavEntry[] => entries;

function SiteSwitcher({ me }: { me: Me }): ReactNode {
  if (me.sites.length <= 1) return null;
  return (
    <label className="flex items-center gap-2">
      <span className="micro-label">Site</span>
      <select
        className="select"
        style={{ height: 32, width: 'auto', maxWidth: 260 }}
        value={me.activeSiteId ?? ''}
        onChange={(event) => {
          void api.post('/me/site', { siteId: event.target.value }).then(() => window.location.reload());
        }}
      >
        {me.sites.map((site) => (
          <option key={site.siteId} value={site.siteId}>
            {site.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function EntitlementBanner({ entitlement }: { entitlement: EntitlementState | null }): ReactNode {
  if (!entitlement) return null;
  if (entitlement.access === 'allowed' && !entitlement.stale && entitlement.warnings.length === 0) return null;
  const tone = entitlement.access === 'no_access' ? 'danger' : entitlement.access === 'read_only' ? 'alert' : 'watch';
  const style = bandStyle(tone === 'danger' ? 'DANGER' : tone === 'alert' ? 'ALERT' : 'WATCH');
  const messages = [
    entitlement.access === 'no_access' ? 'There is no active subscription for this deployment, so analysis is paused. Surveillance data is still being stored.' : null,
    entitlement.access === 'read_only' ? 'The subscription is unsettled: configuration and metered analysis are blocked; alarms continue.' : null,
    entitlement.stale ? `Entitlement information is cached from ${entitlement.fetchedAt ?? 'an earlier lookup'}.` : null,
    ...entitlement.warnings.map((warning) => warning),
  ].filter((entry): entry is string => Boolean(entry));
  if (messages.length === 0) return null;
  return (
    <div className="border-t px-3 py-2 text-cell md:px-6" style={{ borderColor: style.colour, background: 'var(--surface-alt)', color: 'var(--text-secondary)' }} role="status">
      <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: style.colour }} aria-hidden="true" />
      {messages.join(' ')}
      {entitlement.marketplaceUrl ? (
        <a className="link ml-2" href={entitlement.marketplaceUrl} target="_blank" rel="noreferrer">
          Manage subscription
        </a>
      ) : null}
      {entitlement.balanceEur !== null ? (
        <span className="provenance ml-3">balance {money(entitlement.balanceEur * 100)}</span>
      ) : null}
    </div>
  );
}

function LiveIndicator({ state, events }: { state: StreamState; events: number }): ReactNode {
  const colour = state === 'open' ? 'var(--ok)' : state === 'connecting' || state === 'degraded' ? 'var(--watch)' : 'var(--unavailable)';
  const label = state === 'open' ? 'live' : state === 'connecting' ? 'connecting' : state === 'degraded' ? 'polling' : 'offline';
  const title =
    state === 'open'
      ? `streaming, ${events} events this session`
      : state === 'degraded'
        ? `the live stream failed ${ERRORS_BEFORE_POLL} times — refreshing every ${POLL_INTERVAL_MS / 1000} s instead, so the wall stays current but is up to that long behind`
        : state === 'connecting'
          ? 'opening the live stream; the page still shows the last read until it lands'
          : 'not streaming — the page refreshes on navigation instead';
  return (
    <span className="flex items-center gap-2 text-cell text-text-secondary" title={title}>
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: colour }} aria-hidden="true" />
      <span className="hidden sm:inline">{label}</span>
    </span>
  );
}

/** Sticky-nav scroll state (DESIGN.md §7 pattern 1) with the 1 px progress rule under it. */
export function useNavScrollState(): void {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const onScroll = (): void => {
      const node = ref.current ?? document.querySelector<HTMLElement>('.nav');
      if (node) node.dataset.scrolled = String(window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
}
